import { Link, Outlet, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "next-themes";
import { LayoutDashboard, Car, Mail, Users, LogOut, Home, HelpCircle, Menu, GitBranch, UserCircle, ListTodo, Shield, DollarSign, Receipt, MessageSquare, Handshake, Trash2, ClipboardList, Sun, Moon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useState } from "react";
import NotificationBell from "@/components/admin/NotificationBell";

export default function AdminLayout() {
  const { signOut } = useAuth();
  const { theme, setTheme } = useTheme();
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const navItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/admin" },
    { icon: Car, label: "Inventory", path: "/admin/inventory" },
    { icon: DollarSign, label: "Finances", path: "/admin/finances" },
    { icon: Receipt, label: "Expenses", path: "/admin/expenses" },
    { icon: Handshake, label: "Deals", path: "/admin/deals" },
    { icon: UserCircle, label: "Customers", path: "/admin/customers" },
    { icon: Mail, label: "Leads", path: "/admin/leads" },
    { icon: Car, label: "Sell Requests", path: "/admin/sell-requests" },
    { icon: ListTodo, label: "Tasks", path: "/admin/tasks" },
    { icon: MessageSquare, label: "Messages", path: "/admin/messages" },
    { icon: Users, label: "Users", path: "/admin/users" },
    { icon: HelpCircle, label: "FAQs", path: "/admin/faqs" },
    { icon: Trash2, label: "Recycle Bin", path: "/admin/recycle-bin" },
    { icon: ClipboardList, label: "Audit Log", path: "/admin/audit-log" },
    { icon: Shield, label: "Security", path: "/admin/security" },
  ];

  const SidebarContent = () => (
    <>
      <div className="p-6 border-b">
        <h1 className="text-2xl font-heading font-bold text-primary">Car Street</h1>
        <p className="text-sm text-muted-foreground">Admin Portal</p>
      </div>

      <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
        {navItems.map((item) => {
          const isActive = item.path === "/admin" 
            ? location.pathname === "/admin"
            : location.pathname.startsWith(item.path);
          return (
            <Link key={item.path} to={item.path} onClick={() => setIsMobileOpen(false)}>
              <Button
                variant={isActive ? "default" : "ghost"}
                className={cn("w-full justify-start", isActive && "bg-primary text-primary-foreground")}
              >
                <item.icon className="mr-2 h-4 w-4" />
                {item.label}
              </Button>
            </Link>
          );
        })}
      </nav>

      <div className="p-4 border-t space-y-2">
        <Button 
          variant="outline" 
          className="w-full justify-start text-sm md:text-base" 
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
        >
          {theme === "dark" ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
          {theme === "dark" ? "Light Mode" : "Dark Mode"}
        </Button>
        <Link to="/">
          <Button variant="outline" className="w-full justify-start text-sm md:text-base">
            <Home className="mr-2 h-4 w-4" />
            View Website
          </Button>
        </Link>
        <Button variant="ghost" className="w-full justify-start text-sm md:text-base" onClick={signOut}>
          <LogOut className="mr-2 h-4 w-4" />
          Sign Out
        </Button>
      </div>
    </>
  );

  return (
    <div className="flex flex-col md:flex-row h-screen overflow-hidden bg-muted">
        {/* Mobile Header */}
        <div className="md:hidden flex items-center justify-between p-4 bg-card border-b">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-heading font-bold text-primary">Car Street</h1>
          </div>
          <div className="flex items-center gap-1">
            <NotificationBell />
            <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0 flex flex-col bg-card">
              <SidebarContent />
            </SheetContent>
          </Sheet>
          </div>
        </div>

        {/* Desktop Sidebar */}
        <aside className="hidden md:flex w-64 bg-card border-r flex-col">
          <SidebarContent />
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-auto bg-muted">
          <div className="p-4 md:p-8">
            <Outlet />
          </div>
        </main>
    </div>
  );
}
